import React, {useEffect, useState} from "react";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowUp,
  faArrowDown,
  faPlus,
  faPen,
  faTrash,
  faFolderOpen,
  faPaperPlane,
  faUndo,
  faCircleCheck,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {useResourcePersonCourses, useResourcePersonLessons} from "../store/resourcePerson/resourcePersonStore";
import ResourcePersonShell from "../components/resourcePerson/ResourcePersonShell";
import UploadField from "../components/resourcePerson/UploadField";
import EmptyState from "../components/learner/EmptyState";
import Toast from "../components/Toast";
import LessonQuizQuestionsPanel from "../components/resourcePerson/LessonQuizQuestionsPanel";
import {RichTextField, cleanHtmlForSave} from "../components/resourcePerson/RichTextField";
import {shortDate} from "../lib/format";
import RetryBanner from "../components/resourcePerson/RetryBanner";
import {toDisplayString, htmlToText} from "../utils/toDisplayString";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";

const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED", "ALL_LEVELS"];
const EMPTY_FORM = {
  title: "",
  summary: "",
  description: "",
  introduction: "",
  learningIntro: "",
  learningOutcomes: "",
  learningPoints: "",
  requirements: "",
  targetAudience: "",
  targetAudiencePoints: "",
  difficultyLevel: "BEGINNER",
  categoryId: "",
  price: "",
  originalPrice: "",
  hasCertificate: false,
  accessType: "LIFETIME",
  moneyBackDays: "",
  coverImage: "",
};

/** Turn a textarea (one entry per line) into the string[] the API expects. */
const linesToArray = (value) =>
  String(value || "")
    .split("\n")
    .map((entry) => entry.trim())
    .filter(Boolean);

const arrayToLines = (value) => (Array.isArray(value) ? value.join("\n") : "");

const EMPTY_LESSON = {
  title: "",
  contentType: "TEXT",
  content: "",
  fileName: "",
  fileUrl: "",
  filePublicId: "",
  fileCloudType: "",
  fileDeliveryType: "",
  videoTitle: "",
  videoUrl: "",
  assessmentKind: "",
  assessmentTitle: "",
  assessmentInstructions: "",
  assessmentPassThreshold: 70,
  assessmentIsRequired: true,
  description: "",
  position: "",
  isRequired: true,
  isPreview: false,
  moduleId: "",
};

/**
 * Resource-person course builder (Stages 0–2):
 * Stage 0  GET/POST /api/resource-person/categories (dropdown + create)
 * Stage 1  POST /api/resource-person/courses + modules, lessons, assessments,
 *          questions, videos and downloadable resources
 * Stage 2  PATCH …/submit | …/unsubmit (≥1 lesson; 409 locks editing)
 */
const ResourcePersonCourseBuilder = () => {
  const {id, pathId: pathIdFromRoute} = useParams();
  const [searchParams] = useSearchParams();
  const pathId = pathIdFromRoute || searchParams.get("pathId");
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const confirm = useConfirmDialog();

  const {
    courseDetail,
    courseDetailError,
    courseMutationLoading,
    courseMutationError,
    fetchCourseById,
    createCourse,
    updateCourse,
    submitCourse,
    unsubmitCourse,
    deleteCourse,
  } = useResourcePersonCourses();

  const {
    lessons,
    lessonsLoading,
    lessonsError,
    lessonMutationLoading,
    lessonMutationError,
    fetchRpCategories,
    createRpCategory,
    rpCategories,
    fetchLessons,
    createLesson,
    updateLesson,
    deleteLesson,
    reorderLessons,
    createLessonResource,
    fetchLessonResources,
    updateLessonResource,
    deleteLessonResource,
    reorderLessonResources,
    createLessonVideo,
    fetchLessonVideos,
    updateLessonVideo,
    deleteLessonVideo,
    reorderLessonVideos,
    createAssessment,
    fetchAssessments,
    updateAssessment,
    deleteAssessment,
    reorderAssessments,
    // Modules — group lessons within a draft course.
    modules,
    modulesLoading,
    modulesError,
    moduleMutationLoading,
    fetchModules,
    createModule,
    updateModule,
    deleteModule,
    reorderModules,
  } = useResourcePersonLessons();

  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Non-null once a brand-new course’s draft has been created (set on the
  // 409-free success path of saveShell) — drives `hasShell` on step 1.
  const [savedShellId, setSavedShellId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [newCategory, setNewCategory] = useState({name: "", description: "", image: ""});
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [lessonForm, setLessonForm] = useState(EMPTY_LESSON);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [createdAssessmentId, setCreatedAssessmentId] = useState(null);
  const [createdAssessmentKind, setCreatedAssessmentKind] = useState("");
  const [assessmentCreated, setAssessmentCreated] = useState(false);
  const [lessonAssessments, setLessonAssessments] = useState([]);
  const [lessonAttachedResources, setLessonAttachedResources] = useState([]);
  const [lessonAttachedVideos, setLessonAttachedVideos] = useState([]);
  const [assessmentDirty, setAssessmentDirty] = useState(false);
  const [assessmentEditorOpen, setAssessmentEditorOpen] = useState(false);
  // Module authoring: a module groups lessons inside a draft course.
  const [moduleForm, setModuleForm] = useState({title: "", description: ""});
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [moduleBusy, setModuleBusy] = useState(false);
  // The builder is a 2-step wizard (1 · Course shell → 2 · Modules & lessons),
  // not a side-by-side flex/grid. Step 1 is the only gated step: an existing
  // course already has its shell saved, so it starts on step 2.
  const [step, setStep] = useState(isNew ? 1 : 2);
  // Step 2 is a drill-down: module list → one module's lessons → a form. The
  // view REPLACES the step-2 panel instead of opening a modal, so the author
  // only ever has one screen to reason about. null = the module list.
  const [view, setView] = useState(null);

  const fail = (message) =>
    setToast({text: toDisplayString(message, "Request failed") || "Request failed", ok: false});
  const ok = (text) => setToast({text, ok: true});

  // Group lessons under their module for the "2 · Modules & lessons" step. Lessons whose
  // `moduleId` matches no module fall into an "Ungrouped" bucket so nothing is
  // ever hidden from the author.
  const moduleIdOfLesson = (lesson) => lesson.moduleId ?? lesson.module_id ?? "";
  const groupedLessons = (() => {
    const buckets = (modules || []).map((module) => ({
      module,
      items: (lessons || []).filter((lesson) => String(moduleIdOfLesson(lesson)) === String(module.id)),
    }));
    const ungrouped = (lessons || []).filter(
      (lesson) => !(modules || []).some((m) => String(m.id) === String(moduleIdOfLesson(lesson)))
    );
    return {buckets, ungrouped};
  })();

  // ── Step 2 drill-down navigation ────────────────────────────────────────────
  const activeModuleId = view?.moduleId ?? "";
  const activeModule =
    (modules || []).find((m) => String(m.id) === String(activeModuleId)) || null;
  const lessonsInActiveModule = (lessons || []).filter(
    (l) => String(moduleIdOfLesson(l)) === String(activeModuleId)
  );

  const resetModuleForm = () => {
    setEditingModuleId(null);
    setModuleForm({title: "", description: ""});
  };
  const resetLessonForm = () => {
    setEditingLessonId(null);
    setCreatedAssessmentId(null);
    setCreatedAssessmentKind("");
    setAssessmentCreated(false);
    setLessonAssessments([]);
    setLessonAttachedResources([]);
    setLessonAttachedVideos([]);
    setAssessmentDirty(false);
    setAssessmentEditorOpen(false);
    setLessonForm(EMPTY_LESSON);
  };
  /** Back to the module list, clearing both forms. */
  const showModuleList = () => {
    resetModuleForm();
    resetLessonForm();
    setView(null);
  };
  /** Open the create/edit module page. */
  const showModuleForm = (kind) => {
    resetLessonForm();
    setEditingModuleId(null);
    setView({kind});
  };
  /** Drill into one module to manage the lessons inside it. */
  const showLessons = (moduleId) => {
    resetModuleForm();
    resetLessonForm();
    setView({kind: "lessons", moduleId});
  };
  /** Open the create/edit lesson page for a given module. */
  const showLessonForm = (kind, moduleId) => {
    resetModuleForm();
    resetLessonForm();
    // Seed lessonForm.moduleId from the drill-down target. The guard in saveLesson
    // reads lessonForm.moduleId, but the module <select> only renders when
    // !activeModule — so when you drill into a module the picker is hidden and
    // nothing else ever set the field. The request was therefore always blocked
    // with "Group this lesson under a module." and the endpoint never fired.
    if (moduleId) setLessonForm((prev) => ({...prev, moduleId: String(moduleId)}));
    setView({kind, moduleId});
  };
  /**
   * Leave the lesson form. A legacy ungrouped lesson has no module of its own,
   * so fall back to the module just picked in the form — and to the module list
   * if there is still nowhere to go.
   */
  const leaveLessonForm = () => {
    const target = activeModuleId || lessonForm.moduleId;
    resetLessonForm();
    setView(target ? {kind: "lessons", moduleId: target} : null);
  };

  useEffect(() => {
    fetchRpCategories();
  }, [fetchRpCategories]);

  useEffect(() => {
    if (!isNew) fetchCourseById(id);
  }, [id, isNew, fetchCourseById]);

  useEffect(() => {
    if (isNew || !courseDetail) return;
    setForm({
      title: courseDetail.title || "",
      summary: courseDetail.summary || "",
      description: courseDetail.description || "",
      introduction: courseDetail.introduction || "",
      learningIntro: courseDetail.learningIntro || courseDetail.learning_intro || "",
      learningOutcomes: courseDetail.learningOutcomes || courseDetail.learning_outcomes || "",
      learningPoints: arrayToLines(courseDetail.learningPoints || courseDetail.learning_points),
      requirements: courseDetail.requirements || "",
      targetAudience: courseDetail.targetAudience || courseDetail.target_audience || "",
      targetAudiencePoints: arrayToLines(
        courseDetail.targetAudiencePoints || courseDetail.target_audience_points
      ),
      difficultyLevel: courseDetail.difficultyLevel || courseDetail.difficulty_level || "BEGINNER",
      categoryId: courseDetail.categoryId || courseDetail.category_id || courseDetail.category?.id || "",
      price: courseDetail.price ?? "",
      originalPrice: courseDetail.originalPrice ?? courseDetail.original_price ?? "",
      hasCertificate: Boolean(courseDetail.hasCertificate ?? courseDetail.has_certificate),
      accessType: courseDetail.accessType || courseDetail.access_type || "LIFETIME",
      moneyBackDays: courseDetail.moneyBackDays ?? courseDetail.money_back_days ?? "",
      coverImage: courseDetail.coverImage || courseDetail.cover_image || "",
    });
    // The backend rejects every mutation on a non-draft course (409), so the
    // client mirrors that rule exactly: only DRAFT is editable. PENDING,
    // APPROVED and REJECTED are all read-only here. A missing status is treated
    // as DRAFT so older records without the field stay editable.
    const currentStatus = String(courseDetail.status || "").toUpperCase();
    setLocked(currentStatus !== "" && currentStatus !== "DRAFT" && currentStatus !== "REJECTED");
  }, [isNew, courseDetail]);

  useEffect(() => {
    if (!isNew) fetchLessons(id);
  }, [id, isNew, fetchLessons]);

  useEffect(() => {
    if (!isNew) fetchModules(id);
  }, [id, isNew, fetchModules]);

  const saveShell = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return fail("Give the course a title.");
    if (!form.categoryId) return fail("Choose a category first.");
    // Rich-text fields may still hold Quill's "<p><br></p>" placeholder; strip it
    // here (once, at save time) rather than inside the editor render loop.
    const payload = {
      ...form,
      summary: form.summary.trim(),
      description: cleanHtmlForSave(form.description),
      introduction: cleanHtmlForSave(form.introduction),
      learningIntro: cleanHtmlForSave(form.learningIntro),
      learningOutcomes: cleanHtmlForSave(form.learningOutcomes),
      learningPoints: linesToArray(form.learningPoints),
      requirements: cleanHtmlForSave(form.requirements),
      targetAudience: cleanHtmlForSave(form.targetAudience),
      targetAudiencePoints: linesToArray(form.targetAudiencePoints),
      price: form.price === "" ? 0 : Number(form.price),
      originalPrice: form.originalPrice === "" ? null : Number(form.originalPrice),
      hasCertificate: Boolean(form.hasCertificate),
      accessType: form.accessType || "LIFETIME",
      moneyBackDays: form.moneyBackDays === "" ? 0 : Number(form.moneyBackDays),
    };
    const result = isNew ? await createCourse(payload) : await updateCourse(id, payload);
    if (!result.success) {
      if (result.status === 409) setLocked(true);
      return fail(result.error);
    }
    ok(isNew ? "Draft created — now add at least one lesson." : "Draft updated.");
    if (isNew && result.course?.id) setSavedShellId(result.course.id);
    if (isNew && result.course?.id) {
      setStep(2);
      navigate(
        pathId
          ? `/resource-person/content-library/path/${pathId}/course/${result.course.id}`
          : `/resource-person/content-library/course/${result.course.id}/build`,
        {replace: true}
      );
      return;
    }
    if (isNew) {
      // createCourse succeeded but returned no id — without it we cannot route
      // to the builder (modules/lessons live under /course/:id/build), so surface
      // the problem instead of stranding the author on the new-course step.
      return fail(
        "Draft was saved but the server returned no course id. Reload the page and reopen the course from your library."
      );
    }
  };

  const createCategoryInline = async (event) => {
    event.preventDefault();
    if (!newCategory.name.trim()) return fail("Category name is required.");
    setCategoryBusy(true);
    const result = await createRpCategory({
      name: newCategory.name.trim(),
      ...(newCategory.description.trim() ? {description: newCategory.description.trim()} : {}),
      ...(newCategory.image.trim() ? {image: newCategory.image.trim()} : {}),
    });
    setCategoryBusy(false);
    if (!result.success) return fail(result.error);
    setForm((prev) => ({...prev, categoryId: result.category?.id || prev.categoryId}));
    setNewCategory({name: "", description: "", image: ""});
    setCategoryModal(false);
    ok(`Category ready to use — starting lessons.`);
    // Jump straight to the Lessons step once the category exists.
    setStep(2);
  };

  // ── Modules ────────────────────────────────────────────────────────────────
  // A module is a titled container for lessons inside a draft course. Lessons
  // carry a `moduleId`; deleting a module is only allowed while it has none.
  const saveModule = async (event) => {
    event.preventDefault();
    if (!moduleForm.title.trim()) return fail("Module title is required.");
    setModuleBusy(true);
    const payload = {
      title: moduleForm.title.trim(),
      ...(cleanHtmlForSave(moduleForm.description)
        ? {description: cleanHtmlForSave(moduleForm.description)}
        : {}),
      // New modules append after the existing ones.
      ...(editingModuleId ? {} : {position: (modules || []).length + 1}),
    };
    const result = editingModuleId
      ? await updateModule(id, editingModuleId, payload)
      : await createModule(id, payload);
    setModuleBusy(false);
    if (!result.success) return fail(result.error);
    // New modules become the default target for the next lesson, so authoring a
    // "module → its lessons" flow needs no extra clicks.
    if (!editingModuleId && result.module?.id) {
      setLessonForm((prev) => ({...prev, moduleId: String(result.module.id)}));
    }
    // Saving returns to the module list — the form page has done its job.
    const wasEditing = Boolean(editingModuleId);
    showModuleList();
    ok(wasEditing ? "Module updated." : "Module added.");
  };

  const removeModule = async (moduleId) => {
    const lessonCount = (lessons || []).filter(
      (l) => String(moduleIdOfLesson(l)) === String(moduleId)
    ).length;
    if (lessonCount > 0) {
      return fail(`Move or delete this module's ${lessonCount} lesson(s) first (DELETE requires an empty module).`);
    }
    return confirm({
      title: "Delete this module?",
      description: "This action cannot be undone.",
      confirmLabel: "Delete module",
      onConfirm: async () => {
        setModuleBusy(true);
        const result = await deleteModule(id, moduleId);
        setModuleBusy(false);
        if (!result.success) {
          fail(result.error);
          throw new Error(result.error || "Could not delete module.");
        }
        setLessonForm((prev) => (prev.moduleId === moduleId ? {...prev, moduleId: ""} : prev));
        ok("Module deleted.");
      },
    });
  };

  const moveModule = async (index, direction) => {
    const ordered = [...(modules || [])];
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderModules(id, ordered.map((m) => m.id));
    if (!result.success) return fail(result.error);
    ok("Module order saved.");
  };

  const editModule = (module) => {
    // showModuleForm clears the editing id, so set the form state after it.
    showModuleForm("module-edit");
    setEditingModuleId(module.id);
    setModuleForm({title: module.title || "", description: module.description || ""});
  };

  const saveLesson = async (event) => {
    event.preventDefault();
    if (!lessonForm.title.trim()) return fail("Lesson title is required.");
    // Modules are mandatory: a lesson with no moduleId would land in the
    // "Ungrouped" bucket, which the module panel now tells authors is not allowed.
    // activeModuleId is the drill-down target, so fall back to it rather than
    // trusting lessonForm alone — they can disagree.
    const targetModuleId = lessonForm.moduleId || activeModuleId;
    if (!targetModuleId) return fail("Group this lesson under a module.");
    const contentType = lessonForm.contentType || "TEXT";
    if (lessonForm.assessmentKind && !lessonForm.assessmentTitle.trim()) {
      return fail("Enter a title for the assessment.");
    }
    if (
      lessonForm.assessmentKind === "QUIZ" &&
      (!Number.isFinite(Number(lessonForm.assessmentPassThreshold)) ||
        Number(lessonForm.assessmentPassThreshold) < 0 ||
        Number(lessonForm.assessmentPassThreshold) > 100)
    ) {
      return fail("The quiz pass threshold must be between 0 and 100.");
    }
    if (
      lessonForm.assessmentKind &&
      lessonForm.assessmentKind !== "QUIZ" &&
      !lessonForm.assessmentInstructions.trim()
    ) {
      return fail("Enter the question or task for this assessment.");
    }
    if (contentType === "FILE" && (!lessonForm.fileName.trim() || !lessonForm.fileUrl.trim())) {
      return fail("Add a file name and upload or provide a file URL.");
    }
    if (contentType === "VIDEO" && (!lessonForm.videoTitle.trim() || !lessonForm.videoUrl.trim())) {
      return fail("Add a video title and video URL.");
    }
    const payload = {
      title: lessonForm.title.trim(),
      ...(contentType === "TEXT" && cleanHtmlForSave(lessonForm.content)
        ? {content: cleanHtmlForSave(lessonForm.content)}
        : {}),
      // Short summary, distinct from the rich `content` body.
      ...(cleanHtmlForSave(lessonForm.description) ? {description: cleanHtmlForSave(lessonForm.description)} : {}),
      ...(lessonForm.position !== "" ? {position: Number(lessonForm.position)} : {}),
      // Course → modules → lessons. Always sent: targetModuleId is guaranteed
      // non-empty by the guard above.
      moduleId: targetModuleId,
      isRequired: lessonForm.isRequired,
      isPreview: Boolean(lessonForm.isPreview),
    };
    const wasEditing = Boolean(editingLessonId);
    const result = editingLessonId
      ? await updateLesson(id, editingLessonId, payload)
      : await createLesson(id, payload);
    if (!result.success) {
      if (result.status === 409) setLocked(true);
      return fail(result.error);
    }
    const savedLessonId = editingLessonId || result.lesson?.id || result.lesson?._id;
    if (savedLessonId && !String(savedLessonId).startsWith("new-")) {
      setEditingLessonId(savedLessonId);
    }
    if (lessonForm.assessmentKind && (!savedLessonId || String(savedLessonId).startsWith("new-"))) {
      return fail("Lesson saved, but the server did not return its ID. Reopen the lesson before adding an assessment.");
    }
    if (contentType !== "TEXT") {
      if (!savedLessonId || String(savedLessonId).startsWith("new-")) {
        return fail("Lesson saved, but its ID was not returned. Reopen the lesson and retry attaching its media.");
      }
      let attachment;
      if (contentType === "FILE") {
        const existingResult = wasEditing
          ? await fetchLessonResources(id, savedLessonId)
          : {success: true, resources: []};
        if (!existingResult.success) return fail(existingResult.error);
        const existing = existingResult.resources?.[0];
        const mediaPayload = {
          name: lessonForm.fileName.trim(),
          url: lessonForm.fileUrl.trim(),
          publicId: lessonForm.filePublicId.trim(),
          cloudType: lessonForm.fileCloudType.trim(),
          deliveryType: lessonForm.fileDeliveryType.trim(),
          position: 1,
        };
        attachment = existing
          ? await updateLessonResource(id, savedLessonId, existing.id || existing._id, mediaPayload)
          : await createLessonResource(id, savedLessonId, mediaPayload);
      } else {
        const existingResult = wasEditing
          ? await fetchLessonVideos(id, savedLessonId)
          : {success: true, videos: []};
        if (!existingResult.success) return fail(existingResult.error);
        const existing = existingResult.videos?.[0];
        const mediaPayload = {
          title: lessonForm.videoTitle.trim(),
          url: lessonForm.videoUrl.trim(),
          position: 1,
        };
        attachment = existing
          ? await updateLessonVideo(id, savedLessonId, existing.id, mediaPayload)
          : await createLessonVideo(id, savedLessonId, mediaPayload);
      }
      if (!attachment.success) {
        if (!wasEditing) setEditingLessonId(savedLessonId);
        return fail(`Lesson saved, but its ${contentType.toLowerCase()} could not be attached: ${attachment.error}`);
      }
    }
    if (assessmentCreated && createdAssessmentId && assessmentDirty) {
      const assessmentResult = await saveAssessment();
      if (!assessmentResult) return;
    }
    if (lessonForm.assessmentKind && !assessmentCreated) {
      const existingAssessments = await fetchAssessments(id, savedLessonId);
      if (!existingAssessments.success) return fail(existingAssessments.error);
      const assessmentPayload = {
        kind: lessonForm.assessmentKind,
        title: lessonForm.assessmentTitle.trim(),
        instructions: lessonForm.assessmentInstructions.trim(),
        isRequired: Boolean(lessonForm.assessmentIsRequired),
        position: (existingAssessments.assessments || []).length + 1,
        ...(lessonForm.assessmentKind === "QUIZ"
          ? {passThreshold: Number(lessonForm.assessmentPassThreshold)}
          : {}),
      };
      const assessmentResult = await createAssessment(id, savedLessonId, assessmentPayload);
      if (!assessmentResult.success) {
        return fail(`Lesson saved, but its assessment could not be added: ${assessmentResult.error}`);
      }

      let assessmentId = assessmentResult.assessment?.id || assessmentResult.assessment?._id;
      if (!assessmentId || String(assessmentId).startsWith("new-")) {
        const refreshedAssessments = await fetchAssessments(id, savedLessonId);
        if (!refreshedAssessments.success) {
          return fail(`Assessment saved, but it could not be loaded for editing: ${refreshedAssessments.error}`);
        }
        const savedAssessment = [...(refreshedAssessments.assessments || [])]
          .reverse()
          .find((item) =>
            item.title === assessmentPayload.title &&
            String(item.kind || "").toUpperCase() === assessmentPayload.kind
          );
        assessmentId = savedAssessment?.id || savedAssessment?._id;
      }

      setCreatedAssessmentKind(lessonForm.assessmentKind);
      setCreatedAssessmentId(assessmentId || null);
      setAssessmentCreated(true);
      setAssessmentDirty(false);
      setAssessmentEditorOpen(Boolean(assessmentId));
      const refreshedAssessments = await fetchAssessments(id, savedLessonId);
      if (!refreshedAssessments.success) {
        return fail(`Assessment saved, but it could not be loaded: ${refreshedAssessments.error}`);
      }
      setLessonAssessments(refreshedAssessments.assessments || []);
      if (lessonForm.assessmentKind === "QUIZ") {
        if (!assessmentId) {
          return fail("Quiz saved, but the server did not return an assessment ID. Reload this lesson before adding questions.");
        }
        ok("Lesson and quiz saved. Add its questions below.");
        return;
      }
      ok("Lesson and assessment saved.");
      return;
    }
    // Return to the module whose lesson list the author drilled in from.
    // `moduleId` is guaranteed non-empty by the guard above.
    showLessons(targetModuleId);
    ok(wasEditing ? "Lesson updated." : "Lesson added.");
  };

  const moveLesson = async (index, direction, moduleId) => {
    if (!moduleId) return fail("Move this lesson into a module before reordering it.");
    const ordered = (lessons || []).filter(
      (lesson) => String(moduleIdOfLesson(lesson)) === String(moduleId)
    );
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderLessons(id, moduleId, ordered.map((lesson) => lesson.id));
    if (!result.success) return fail(result.error);
    ok("Lesson order saved.");
  };

  const editLesson = async (lesson, moduleId) => {
    const [resourcesResult, videosResult, assessmentsResult] = await Promise.all([
      fetchLessonResources(id, lesson.id),
      fetchLessonVideos(id, lesson.id),
      fetchAssessments(id, lesson.id),
    ]);
    if (!resourcesResult.success) return fail(resourcesResult.error);
    if (!videosResult.success) return fail(videosResult.error);
    if (!assessmentsResult.success) return fail(assessmentsResult.error);

    setLessonAttachedResources(resourcesResult.resources || []);
    setLessonAttachedVideos(videosResult.videos || []);
    const file = resourcesResult.resources?.[0];
    const video = videosResult.videos?.[0];
    const legacyType = String(lesson.type || "").toUpperCase();
    const contentType = video
      ? "VIDEO"
      : file
        ? "FILE"
        : legacyType === "VIDEO"
          ? "VIDEO"
          : ["FILE", "DOCUMENT"].includes(legacyType)
            ? "FILE"
            : "TEXT";

    showLessonForm("lesson-edit", moduleId);
    setEditingLessonId(lesson.id);
    setLessonForm({
      ...EMPTY_LESSON,
      title: lesson.title || "",
      contentType,
      content: lesson.content || "",
      fileName: file?.name || "",
      fileUrl: file?.url || "",
      filePublicId: file?.publicId || file?.public_id || "",
      fileCloudType: file?.cloudType || file?.cloud_type || "",
      fileDeliveryType: file?.deliveryType || file?.delivery_type || "",
      videoTitle: video?.title || "",
      videoUrl: video?.url || "",
      description: lesson.description || "",
      position: lesson.position ?? "",
      isRequired: lesson.isRequired ?? true,
      isPreview: Boolean(lesson.isPreview ?? lesson.is_preview),
      moduleId,
    });
    const existingAssessments = assessmentsResult.assessments || [];
    setLessonAssessments(existingAssessments);
    setAssessmentEditorOpen(false);
    const firstAssessment = existingAssessments[0];
    if (firstAssessment) {
      setCreatedAssessmentId(firstAssessment.id || firstAssessment._id || null);
      setCreatedAssessmentKind(String(firstAssessment.kind || "").toUpperCase());
      setAssessmentCreated(true);
      setLessonForm((current) => ({
        ...current,
        assessmentKind: String(firstAssessment.kind || "").toUpperCase(),
        assessmentTitle: firstAssessment.title || "",
        assessmentInstructions: firstAssessment.instructions || "",
        assessmentPassThreshold: firstAssessment.passThreshold ?? firstAssessment.pass_threshold ?? 70,
        assessmentIsRequired: firstAssessment.isRequired ?? firstAssessment.is_required ?? true,
      }));
    }
    setAssessmentDirty(false);
  };

  const selectAssessment = (assessment) => {
    setCreatedAssessmentId(assessment.id || assessment._id || null);
    setCreatedAssessmentKind(String(assessment.kind || "").toUpperCase());
    setAssessmentCreated(true);
    setAssessmentDirty(false);
    setAssessmentEditorOpen(true);
    setLessonForm((current) => ({
      ...current,
      assessmentKind: String(assessment.kind || "").toUpperCase(),
      assessmentTitle: assessment.title || "",
      assessmentInstructions: assessment.instructions || "",
      assessmentPassThreshold: assessment.passThreshold ?? assessment.pass_threshold ?? 70,
      assessmentIsRequired: assessment.isRequired ?? assessment.is_required ?? true,
    }));
  };

  const saveAssessment = async () => {
    if (!editingLessonId || !createdAssessmentId) {
      fail("Save the lesson before editing its assessment.");
      return false;
    }
    const payload = {
      kind: lessonForm.assessmentKind,
      title: lessonForm.assessmentTitle.trim(),
      instructions: lessonForm.assessmentInstructions.trim(),
      isRequired: Boolean(lessonForm.assessmentIsRequired),
      position: lessonAssessments.find((item) => String(item.id || item._id) === String(createdAssessmentId))?.position || 1,
      ...(lessonForm.assessmentKind === "QUIZ"
        ? {passThreshold: Number(lessonForm.assessmentPassThreshold)}
        : {}),
    };
    const result = await updateAssessment(id, editingLessonId, createdAssessmentId, payload);
    if (!result.success) {
      fail(result.error);
      return false;
    }
    setLessonAssessments((current) =>
      current.map((item) =>
        String(item.id || item._id) === String(createdAssessmentId) ? {...item, ...payload} : item
      )
    );
    setAssessmentDirty(false);
    ok("Assessment updated.");
    return true;
  };

  const changeAssessmentForm = (field, value) => {
    setLessonForm((current) => ({...current, [field]: value}));
    if (createdAssessmentId) setAssessmentDirty(true);
  };

  const startNewAssessment = () => {
    setCreatedAssessmentId(null);
    setCreatedAssessmentKind("");
    setAssessmentCreated(false);
    setAssessmentDirty(false);
    setLessonForm((current) => ({
      ...current,
      assessmentKind: "",
      assessmentTitle: "",
      assessmentInstructions: "",
      assessmentPassThreshold: 70,
      assessmentIsRequired: true,
    }));
  };

  const removeAssessment = async (assessment) => {
    const assessmentId = assessment.id || assessment._id;
    if (!assessmentId || !editingLessonId) return fail("This assessment cannot be deleted because its ID is missing.");
    return confirm({
      title: `Delete "${assessment.title || assessment.kind}"?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete assessment",
      onConfirm: async () => {
        const result = await deleteAssessment(id, editingLessonId, assessmentId);
        if (!result.success) {
          fail(result.error);
          throw new Error(result.error || "Could not delete assessment.");
        }
        const remaining = lessonAssessments.filter(
          (item) => String(item.id || item._id) !== String(assessmentId)
        );
        setLessonAssessments(remaining);
        if (String(createdAssessmentId) === String(assessmentId)) startNewAssessment();
        ok("Assessment deleted.");
      },
    });
  };

  const moveAssessment = async (index, direction) => {
    if (!editingLessonId) return fail("Save the lesson before reordering its assessments.");
    const target = index + direction;
    if (target < 0 || target >= lessonAssessments.length) return;
    const ordered = [...lessonAssessments];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderAssessments(
      id,
      editingLessonId,
      ordered.map((item) => item.id || item._id)
    );
    if (!result.success) return fail(result.error);
    setLessonAssessments(ordered.map((item, position) => ({ ...item, position: position + 1 })));
    ok("Assessment order saved.");
  };

  const removeAttachedResource = async (resource) => {
    if (!editingLessonId || !resource?.id) return fail("This resource cannot be deleted.");
    const result = await deleteLessonResource(id, editingLessonId, resource.id);
    if (!result.success) return fail(result.error);
    setLessonAttachedResources((current) => current.filter((item) => item.id !== resource.id));
    if (lessonForm.fileUrl && resource.url === lessonForm.fileUrl) {
      setLessonForm((current) => ({
        ...current,
        fileUrl: "",
        filePublicId: "",
        fileCloudType: "",
        fileDeliveryType: "",
      }));
    }
    ok("Lesson resource deleted.");
  };

  const moveAttachedResource = async (index, direction) => {
    if (!editingLessonId) return fail("Save the lesson before reordering its resources.");
    const target = index + direction;
    if (target < 0 || target >= lessonAttachedResources.length) return;
    const ordered = [...lessonAttachedResources];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderLessonResources(
      id,
      editingLessonId,
      ordered.map((item) => item.id)
    );
    if (!result.success) return fail(result.error);
    setLessonAttachedResources(ordered.map((item, position) => ({ ...item, position: position + 1 })));
    ok("Resource order saved.");
  };

  const removeAttachedVideo = async (video) => {
    if (!editingLessonId || !video?.id) return fail("This video cannot be deleted.");
    const result = await deleteLessonVideo(id, editingLessonId, video.id);
    if (!result.success) return fail(result.error);
    setLessonAttachedVideos((current) => current.filter((item) => item.id !== video.id));
    if (lessonForm.videoUrl && video.url === lessonForm.videoUrl) {
      setLessonForm((current) => ({ ...current, videoUrl: "" }));
    }
    ok("Lesson video deleted.");
  };

  const moveAttachedVideo = async (index, direction) => {
    if (!editingLessonId) return fail("Save the lesson before reordering its videos.");
    const target = index + direction;
    if (target < 0 || target >= lessonAttachedVideos.length) return;
    const ordered = [...lessonAttachedVideos];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderLessonVideos(
      id,
      editingLessonId,
      ordered.map((item) => item.id)
    );
    if (!result.success) return fail(result.error);
    setLessonAttachedVideos(ordered.map((item, position) => ({ ...item, position: position + 1 })));
    ok("Video order saved.");
  };

  const submit = async () => {
    if ((lessons || []).length < 1) return fail("Add at least one lesson before submitting.");
    const result = await submitCourse(id);
    if (!result.success) return fail(result.error);
    await fetchCourseById(id);
    setLocked(true);
    ok("Sent for review — you can withdraw any time to make more changes.");
  };

  const unsubmit = async () => {
    const result = await unsubmitCourse(id);
    if (!result.success) return fail(result.error);
    await fetchCourseById(id);
    setLocked(false);
    ok("Withdrawn from review — you can edit again.");
  };

  const removeCourse = async () => {
    return confirm({
      title: "Delete this draft course?",
      description: "This action cannot be undone.",
      confirmLabel: "Delete course",
      onConfirm: async () => {
        const result = await deleteCourse(id);
        if (!result.success) {
          fail(result.error);
          throw new Error(result.error || "Could not delete course.");
        }
        navigate("/resource-person/content-library", {replace: true});
      },
    });
  };

  const status = String(courseDetail?.status || (isNew ? "DRAFT" : "")).toUpperCase();
  const hasShell = isNew ? Boolean(savedShellId) : true;
  const lessonCount = (lessons || []).length;
  const moduleCount = (modules || []).length;
  const moduleLabel = `${moduleCount} module${moduleCount === 1 ? "" : "s"}`;
  const lessonLabel = `${lessonCount} lesson${lessonCount === 1 ? "" : "s"}`;
  const STEPS = [
    {n: 1, label: "Course shell", hint: "Title, category & details"},
    {
      n: 2,
      // Step 2 authors BOTH the modules and their lessons, modules first, so
      // naming it "Lessons" alone contradicted the panel the author sees first.
      label: "Modules & lessons",
      hint: moduleCount || lessonCount ? `${moduleLabel} · ${lessonLabel}` : "Add modules or lessons",
    },
  ];
  const fieldClass =
    "w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-300 disabled:bg-slate-100";
  const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";
  const primaryBtn =
    "inline-flex w-fit items-center gap-2 rounded-md bg-sidebar-primary px-4 py-2 text-sm font-semibold text-sidebar-primary-foreground hover:bg-sidebar-accent disabled:cursor-not-allowed disabled:opacity-60";
  const ghostBtn =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60";
  // Compact variants of the two styles above, for the step-2 header row only.
  // Only the HORIZONTAL padding is trimmed (px): the vertical padding gives the
  // buttons their clickable height, and the header row already fits. Step 1
  // keeps using primaryBtn/ghostBtn.
  const compactPrimaryBtn =
    "inline-flex w-fit items-center gap-1.5 rounded-md bg-sidebar-primary px-3 py-2 text-xs font-semibold text-sidebar-primary-foreground hover:bg-sidebar-accent disabled:cursor-not-allowed disabled:opacity-60";
  const compactGhostBtn =
    "inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60";
  const lessonFormDisabled = lessonMutationLoading || locked;

  /**
   * Render one lesson row. Grouping only affects where the row is placed, so
   * the up/down controls use the lesson's index in the flat `lessons` array —
   * that is the order `PATCH …/lessons/reorder { lessonIds }` expects.
   */
  const renderLessonRow = (lesson) => {
    const flatIndex = (lessons || []).findIndex((l) => l.id === lesson.id);
    const moduleId = moduleIdOfLesson(lesson);
    const moduleLessons = (lessons || []).filter(
      (item) => String(moduleIdOfLesson(item)) === String(moduleId)
    );
    const moduleIndex = moduleLessons.findIndex((item) => item.id === lesson.id);
    const moduleTitle = (modules || []).find((m) => String(m.id) === String(moduleId))?.title;
    // min-w-0 below is load-bearing: this row is the grid item for both the
    // module's lesson list and the ungrouped list, and grid items default to
    // min-width:auto — without it the row bursts out of its panel instead of
    // shrinking to fit.
    return (
      <li key={lesson.id} className="min-w-0 rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="m-0 truncate text-sm font-semibold text-slate-900">
              {flatIndex + 1}. {lesson.title}
            </p>
            <p className="m-0 mt-0.5 text-xs text-slate-500">
              {lesson.type || "LESSON"}
              {moduleTitle ? ` · ${moduleTitle}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1">
            <button type="button" className={ghostBtn} title="Move up"
              disabled={locked || lessonMutationLoading || !moduleId || moduleIndex === 0}
              onClick={() => moveLesson(moduleIndex, -1, moduleId)}><FontAwesomeIcon icon={faArrowUp} /></button>
            <button type="button" className={ghostBtn} title="Move down"
              disabled={locked || lessonMutationLoading || !moduleId || moduleIndex === moduleLessons.length - 1}
              onClick={() => moveLesson(moduleIndex, 1, moduleId)}><FontAwesomeIcon icon={faArrowDown} /></button>
            <button type="button" className={ghostBtn} title="Edit" disabled={locked}
              onClick={() => editLesson(lesson, moduleId)}><FontAwesomeIcon icon={faPen} /></button>
            <button type="button" className={ghostBtn} title="Delete" disabled={locked || lessonMutationLoading}
              onClick={() => confirm({
                title: `Delete "${lesson.title}"?`,
                description: "This action cannot be undone.",
                confirmLabel: "Delete lesson",
                onConfirm: async () => {
                  const result = await deleteLesson(id, lesson.id);
                  if (!result.success) {
                    fail(result.error);
                    throw new Error(result.error || "Could not delete lesson.");
                  }
                  ok("Lesson deleted.");
                },
              })}><FontAwesomeIcon icon={faTrash} /></button>
          </div>
        </div>

      </li>
    );
  };


  return (
    <ResourcePersonShell
      title={isNew ? "New course" : form.title || "Course builder"}
      subtitle={
        isNew
          ? "Create the course shell, then add modules and lessons"
          : `${locked ? "Read-only while this course is under review" : "Draft — you can edit freely"} · ${status || "DRAFT"}`
      }
      icon={faFolderOpen}
      actions={
        <>
          <button
            type="button"
            className={ghostBtn}
            onClick={() =>
              navigate(
                pathId
                  ? `/resource-person/content-library/path/${pathId}/build`
                  : "/resource-person/content-library"
              )
            }
          >
            <FontAwesomeIcon icon={faArrowLeft} /> {pathId ? "Back to learning path" : "Library"}
          </button>
          {!isNew ? (
            <button type="button" className={ghostBtn} onClick={removeCourse}>
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          ) : null}
        </>
      }
      >
        {toast ? <Toast message={toast.text} onClose={() => setToast(null)} isSuccess={toast.ok} /> : null}
      {/* Load failure: the author has no other way to re-fetch, so offer a retry.
          Save failure (below) gets no button — the submit button is still there. */}
      {courseDetailError && !isNew ? (
        <RetryBanner message={courseDetailError} onRetry={() => fetchCourseById(id)} />
      ) : null}
      {courseMutationError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{courseMutationError}</p>
      ) : null}

        {/* Stepper navigation — replaces the old side-by-side grid so the shell
          and lessons read as sequential steps rather than a flex row. */}
      <nav aria-label="Course builder steps" className="mb-5 rounded-xs border border-slate-200 bg-white p-4 shadow-sm">
        <ol className="m-0 flex list-none flex-col gap-3 p-0 sm:flex-row sm:items-center">
          {STEPS.map((item, index) => {
            const complete = item.n === 1 ? hasShell : lessonCount > 0;
            const current = step === item.n;
            return (
              <React.Fragment key={item.n}>
                <li className="flex min-w-0 items-center">
                  <button
                    type="button"
                    onClick={() => setStep(item.n)}
                    aria-current={current ? "step" : undefined}
                    className={`flex min-w-0 items-center gap-3 rounded-xs px-1 py-0.5 text-left ${
                      item.n === 1 ? "" : "sm:ml-0"
                    } ${hasShell || item.n === 1 ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                    disabled={item.n === 2 && !hasShell}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        complete
                          ? "bg-emerald-600 text-white"
                          : current
                          ? "bg-blue-900 text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {complete && !current ? <FontAwesomeIcon icon={faCircleCheck} /> : item.n}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-slate-900">{item.label}</span>
                      <span className="block truncate text-xs text-slate-500">{item.hint}</span>
                    </span>
                  </button>
                </li>
                {index < STEPS.length - 1 ? (
                  <li aria-hidden="true" className={`hidden h-0.5 flex-1 rounded-full sm:block ${complete && hasShell ? "bg-emerald-600" : "bg-slate-200"}`} />
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>

      <section className="rounded-xs border border-slate-200 bg-white p-5 shadow-sm">
        {step === 1 ? (
          <>
          <h2 className="m-0 text-sm font-bold text-slate-900">1 · Course shell</h2>
          <p className="m-0 mt-1 text-xs text-slate-500">
            Save the course shell first — you can add modules and lessons in the next step.
          </p>
          <form className="mt-4 grid gap-3" onSubmit={saveShell}>
            <div>
              <label className={labelClass} htmlFor="rpc-category">Category</label>
              <div className="flex items-center gap-2">
                <select id="rpc-category" className={`${fieldClass} w-1/2`} value={form.categoryId} disabled={locked}
                  onChange={(e) => setForm({...form, categoryId: e.target.value})}>
                  <option value="">Choose a category…</option>
                  {(rpCategories || []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}{category.courseCount != null ? ` (${category.courseCount})` : ""}
                    </option>
                  ))}
                </select>
                <button type="button" className={ghostBtn} disabled={locked} onClick={() => setCategoryModal(true)} title="Create a category">
                  Create category
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="rpc-title">Title</label>
              <input id="rpc-title" className={fieldClass} value={form.title} disabled={locked}
                onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Introduction to Programming" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className={labelClass} htmlFor="rpc-difficulty">Difficulty</label>
                <select id="rpc-difficulty" className={fieldClass} value={form.difficultyLevel} disabled={locked}
                  onChange={(e) => setForm({...form, difficultyLevel: e.target.value})}>
                  {DIFFICULTIES.map((level) => <option key={level} value={level}>{level}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="rpc-price">Current price (₦)</label>
                <input id="rpc-price" type="number" min="0" className={fieldClass} value={form.price} disabled={locked}
                  onChange={(e) => setForm({...form, price: e.target.value})} placeholder="0 = free" />
              </div>
              <div>
                <label className={labelClass} htmlFor="rpc-original-price">Old price (₦)</label>
                <input id="rpc-original-price" type="number" min="0" className={fieldClass} value={form.originalPrice} disabled={locked}
                  onChange={(e) => setForm({...form, originalPrice: e.target.value})} placeholder="Optional crossed-out price" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <RichTextField
                id="rpc-description"
                value={form.description}
                disabled={locked}
                placeholder="Describe the course. Formatting is supported and the box grows as you type or paste."
                onChange={(description) => setForm({...form, description})}
              />
              <p className="m-0 mt-1 text-xs text-slate-500">
                Bold, italic, headings, lists, links and colour are supported.
              </p>
            </div>
            <div>
              <label className={labelClass}>Introduction</label>
              <RichTextField
                id="rpc-intro"
                value={form.introduction}
                disabled={locked}
                minHeight={100}
                placeholder="Introduce the course — what it covers and who it is for."
                onChange={(introduction) => setForm({...form, introduction})}
              />
            </div>
            <div>
              <label className={labelClass}>Learning outcomes</label>
              <RichTextField
                id="rpc-outcomes"
                value={form.learningOutcomes}
                disabled={locked}
                minHeight={100}
                placeholder="What learners will be able to do by the end."
                onChange={(learningOutcomes) => setForm({...form, learningOutcomes})}
              />
            </div>
            <div>
              <label className={labelClass}>Requirements</label>
              <RichTextField
                id="rpc-reqs"
                value={form.requirements}
                disabled={locked}
                minHeight={100}
                placeholder="Anything learners need before starting."
                onChange={(requirements) => setForm({...form, requirements})}
              />
            </div>
            <div>
              <label className={labelClass}>Who it is for</label>
              <RichTextField
                id="rpc-audience"
                value={form.targetAudience}
                disabled={locked}
                minHeight={100}
                placeholder="The audience this course is written for."
                onChange={(targetAudience) => setForm({...form, targetAudience})}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.hasCertificate)}
                  disabled={locked}
                  onChange={(e) => setForm({...form, hasCertificate: e.target.checked})}
                  className="h-4 w-4 accent-[#16213A]"
                />
                Certificate on completion
              </label>
              <div>
                <label className={labelClass} htmlFor="rpc-access-type">Access type</label>
                <select
                  id="rpc-access-type"
                  className={fieldClass}
                  value={form.accessType}
                  disabled={locked}
                  onChange={(e) => setForm({...form, accessType: e.target.value})}
                >
                  <option value="LIFETIME">Lifetime</option>
                  <option value="LIMITED">Limited</option>
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="rpc-money-back">Money-back days</label>
                <input
                  id="rpc-money-back"
                  type="number"
                  min="0"
                  className={fieldClass}
                  value={form.moneyBackDays}
                  disabled={locked}
                  onChange={(e) => setForm({...form, moneyBackDays: e.target.value})}
                  placeholder="0 = no refund window"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Cover image</label>
              <UploadField kind="image" folder="course-covers" label="" accept="image/*"
                value={form.coverImage} disabled={locked}
                onChange={(url) => setForm((prev) => ({...prev, coverImage: url}))} />
            </div>
          </form>

          {/* Category modal — replaces the old inline form so the “+” button
              next to the category select opens a focused dialog. */}
          {categoryModal ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="w-full max-w-md rounded-xs border border-slate-200 bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between">
                  <h3 className="m-0 text-sm font-bold text-slate-900">Create a category</h3>
                  <button type="button" className={ghostBtn} onClick={() => setCategoryModal(false)} title="Close">
                    ✕
                  </button>
                </div>
                <p className="m-0 mt-1 text-xs text-slate-500">
                  It becomes available to pick straight away.
                </p>
                <form className="mt-4 grid gap-3" onSubmit={createCategoryInline}>
                  <div>
                    <label className={labelClass} htmlFor="rpc-cat-name">Category name</label>
                    <input id="rpc-cat-name" className={fieldClass} value={newCategory.name} disabled={categoryBusy}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})} placeholder="e.g. Web Development" />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="rpc-cat-desc">Description (optional)</label>
                    <input id="rpc-cat-desc" className={fieldClass} value={newCategory.description} disabled={categoryBusy}
                      onChange={(e) => setNewCategory({...newCategory, description: e.target.value})} placeholder="Short blurb" />
                  </div>
                  <div>
                    <label className={labelClass}>Image (optional)</label>
                    <UploadField kind="image" folder="category-images" label="" accept="image/*"
                      value={newCategory.image} disabled={categoryBusy}
                      onChange={(url) => setNewCategory((prev) => ({...prev, image: url}))} />
                  </div>
                  <div className="mt-1 flex justify-end gap-2">
                    <button type="button" className={ghostBtn} onClick={() => setCategoryModal(false)} disabled={categoryBusy}>
                      Cancel
                    </button>
                    <button type="submit" className={primaryBtn} disabled={categoryBusy}>
                      <FontAwesomeIcon icon={faPlus} /> {categoryBusy ? "Creating…" : "Create category"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
          {locked ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2">
              <p className="m-0 flex items-center gap-2 text-xs text-amber-700">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {status === "PENDING"
                  ? "This course is waiting for review, so it's read-only right now."
                  : status === "APPROVED"
                    ? "This course is approved and live for learners, so it's read-only."
                    : status === "REJECTED"
                      ? "This course was sent back with changes requested, so it's read-only."
                      : "This course is read-only right now."}
              </p>
              {status === "PENDING" ? (
                <button type="button" className={ghostBtn} onClick={unsubmit}>
                  <FontAwesomeIcon icon={faUndo} /> Withdraw to keep editing
                </button>
              ) : (
                <button type="button" className={ghostBtn} onClick={() => setStep(2)}>
                  View content
                </button>
              )}
            </div>
          ) : null}
          {/* Next → Lessons. Always rendered on step 1 so the wizard is
              navigable; for a brand-new course it is disabled until the
              shell draft is saved (hasShell flips once id exists). */}
          <div className="mt-5 flex gap-3 border-t border-slate-100 pt-4">
            <button type="button" className={`${primaryBtn} flex-1 justify-center`} disabled={locked} onClick={saveShell}>
              <FontAwesomeIcon icon={faCircleCheck} /> {isNew ? "Create draft" : "Save draft"}
            </button>
            <button type="button" className={`${primaryBtn} flex-1 justify-center`} disabled={!hasShell} onClick={() => setStep(2)}>
              Continue to lessons <FontAwesomeIcon icon={faArrowDown} />
            </button>
          </div>
          {!hasShell ? (
            <p className="m-0 mt-2 text-xs text-slate-500">
              Save the draft first — lessons attach to an existing course.
            </p>
          ) : null}
          </>
        ) : (
          <>
          {/* Text and buttons stay side by side. The parent is flex-nowrap and
              the title block is flex-1, so the text absorbs the leftover width
              (wrapping inside its own box) instead of pushing the buttons onto
              a second line — which is what flex-wrap used to do here. The
              button group is flex-shrink-0 so it is never squeezed either. */}
          <div className="flex flex-nowrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-sm font-bold text-slate-900">
                2 · Modules &amp; lessons ({moduleLabel}, {lessonLabel})
              </h2>
              <p className="m-0 mt-1 text-xs text-slate-500">VIDEO needs a YouTube videoUrl · DOCUMENT needs a file URL.</p>
            </div>
            <div className="ml-auto flex flex-shrink-0 flex-wrap justify-end gap-2.5">
              {/* Leaving for the shell also leaves the drill-down, so returning
                  to step 2 lands on the module list rather than a stale form. */}
              <button type="button" className={compactGhostBtn}
                onClick={() => {showModuleList(); setStep(1);}}>
                <FontAwesomeIcon icon={faArrowUp} /> Shell
              </button>
              {/* The drill-down CTA. It changes with the screen: the module list
                  offers "Create module", a module's lesson list offers
                  "Add lesson". Hidden on the form screens, which have their own
                  Save/Cancel. */}
              {!isNew && !locked && (view === null || view.kind === "lessons") ? (
                <button
                  type="button"
                  className={compactPrimaryBtn}
                  onClick={() =>
                    view?.kind === "lessons"
                      ? showLessonForm("lesson-create", view.moduleId)
                      : showModuleForm("module-create")
                  }
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {view?.kind === "lessons" ? "Add lesson" : "Create module"}
                </button>
              ) : null}
              {!isNew ? (
                <>
                  {(status === "DRAFT" || status === "REJECTED") ? (
                    <button type="button" className={compactPrimaryBtn} onClick={submit}>
                      <FontAwesomeIcon icon={faPaperPlane} /> Submit for review
                    </button>
                  ) : null}
                  {status === "PENDING" ? (
                    <button type="button" className={compactGhostBtn} onClick={unsubmit}>
                      <FontAwesomeIcon icon={faUndo} /> Withdraw to keep editing
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          {isNew ? (
            <div className="mt-3">
              <EmptyState icon={faFolderOpen} title="Save the shell first" hint="Create the draft above." />
            </div>
          ) : (
            <>
              {!view ? (
                /* ── Module list ───────────────────────────────────────────── */
                <div className="mt-4 rounded-sm border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="m-0 text-sm font-bold text-slate-900">Modules [{moduleCount}]</h3>
                      <p className="m-0 mt-0.5 text-xs text-red-400">" Every lesson must be grouped under a module. </p>
                      <p className="m-0 mt-0.5 mb-3 text-xs text-red-400">" Click a module to add lessons to it. </p>
                    </div>
                    {modulesLoading ? <span className="text-xs text-slate-400">Loading…</span> : null}
                  </div>

                  {modulesError ? (
                    <RetryBanner message={modulesError} size="xs" className="m-0 mt-2" onRetry={() => fetchModules(id)} />
                  ) : null}

                  {moduleCount > 0 ? (
                    <ol className="m-0 mt-2 grid list-none gap-1.5 p-0">
                      {(modules || []).map((module, index) => {
                        const count = (lessons || []).filter(
                          (l) => String(moduleIdOfLesson(l)) === String(module.id)
                        ).length;
                        // min-w-0 below is load-bearing: this <li> is a grid item,
                        // and grid items default to min-width:auto, so the row
                        // could never shrink below its content and burst out of
                        // the panel. With min-w-0 the flex children wrap instead.
                        return (
                          <li key={module.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2.5">
                            {/* The row is the "click a module to add lessons to it"
                                target. Browsing stays enabled on a locked course
                                so a submitted draft can still be reviewed; the
                                per-row actions below are what `locked` disables. */}
                            <button
                              type="button"
                              onClick={() => showLessons(module.id)}
                              className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
                            >
                              <p className="m-0 truncate text-xs font-semibold text-slate-800">
                                {index + 1}. {module.title}
                              </p>
                              {/* The lesson count moved to the bottom-right of the row
                                  (beneath the action icons); only the description stays
                                  on this line now. */}
                              {module.description ? (

                                <span className="mt-2 block whitespace-pre-line rounded-sm px-2 py-1.5 text-[11px] leading-snug text-slate-600">
                                  {htmlToText(module.description)}
                                </span>
                              ) : null}
                            </button>
                            {/* Icons on top, lesson count directly beneath them, so the
                                count sits in the bottom-right corner of the row. ml-auto
                                keeps the whole group right even when the row wraps. The
                                icon buttons themselves are left exactly as they were. */}
                            {/* self-end pins the whole column (icons + count) to the
                                BOTTOM edge of the row. The <li> is items-center, so
                                without it both would float at the vertical middle. */}
                            <div className="ml-auto flex self-end flex-col items-end gap-1">
                              <div className="flex flex-wrap items-center justify-end gap-1">
                                <button type="button" className={ghostBtn} title="Move up"
                                  disabled={locked || moduleMutationLoading || moduleBusy || index === 0}
                                  onClick={() => moveModule(index, -1)}><FontAwesomeIcon icon={faArrowUp} /></button>
                                <button type="button" className={ghostBtn} title="Move down"
                                  disabled={locked || moduleMutationLoading || moduleBusy || index === moduleCount - 1}
                                  onClick={() => moveModule(index, 1)}><FontAwesomeIcon icon={faArrowDown} /></button>
                                <button type="button" className={ghostBtn} title="Rename" disabled={locked || moduleBusy}
                                  onClick={() => editModule(module)}><FontAwesomeIcon icon={faPen} /></button>
                                <button type="button" className={ghostBtn} title="Delete" disabled={locked || moduleBusy}
                                  onClick={() => removeModule(module.id)}><FontAwesomeIcon icon={faTrash} /></button>
                              </div>
                              <span className="text-[11px] text-slate-500">
                                {count} lesson{count === 1 ? "" : "s"}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <p className="m-0 mt-2 text-xs text-slate-500">
                      No modules yet — create one to start adding lessons.
                    </p>
                  )}

                  {/* Lessons saved before modules were required still carry no
                      moduleId. Keep them reachable here so none are hidden. */}
                  {groupedLessons.ungrouped.length > 0 ? (
                    <div className="mt-3 border-t border-slate-100 pt-2">
                      <p className="m-0 mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                        Ungrouped — needs a module · {groupedLessons.ungrouped.length}
                      </p>
                      <ol className="m-0 grid list-none gap-2 p-0">
                        {groupedLessons.ungrouped.map((lesson) => renderLessonRow(lesson))}
                      </ol>
                    </div>
                  ) : null}
                </div>
              ) : view.kind === "lessons" ? (
                /* ── One module's lessons ──────────────────────────────────── */
                <div className="mt-4 rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="m-0 truncate text-xs font-bold text-slate-900">
                        {activeModule?.title || "Module"}
                      </h3>
                      <p className="m-0 mt-0.5 text-xs text-slate-500">
                        {lessonsInActiveModule.length} lesson{lessonsInActiveModule.length === 1 ? "" : "s"} in this module.
                      </p>
                    </div>
                    <button type="button" className={`${ghostBtn} ml-auto`} onClick={showModuleList}>
                      <FontAwesomeIcon icon={faArrowLeft} /> All modules
                    </button>
                  </div>

                  {lessonsError ? (
                    <RetryBanner message={lessonsError} size="xs" className="m-0 mt-2" onRetry={() => fetchLessons(id)} />
                  ) : null}

                  {lessonsLoading && (lessons || []).length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">Loading lessons…</p>
                  ) : lessonsInActiveModule.length === 0 ? (
                    <div className="mt-3">
                      <EmptyState
                        icon={faFolderOpen}
                        title="No lessons in this module yet"
                        hint="Use “Add lesson” at the top right to open the form, then “Create lesson” at the bottom to save it."
                      />
                    </div>
                  ) : (
                    <ol className="m-0 mt-3 grid list-none gap-2 p-0">
                      {lessonsInActiveModule.map((lesson) => renderLessonRow(lesson))}
                    </ol>
                  )}
                </div>
              ) : view.kind === "module-create" || view.kind === "module-edit" ? (
                /* ── Create / edit module ──────────────────────────────────── */
                <div className="mt-4 rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h3 className="m-0 text-xs font-bold text-slate-900">
                      {view.kind === "module-edit" ? "Edit module" : "Create a module"}
                    </h3>
                    <button type="button" className={`${ghostBtn} ml-auto`} onClick={showModuleList}>Cancel</button>
                  </div>
                  <form className="mt-3 grid gap-2" onSubmit={saveModule}>
                    <div>
                      <span className={labelClass}>Module title</span>
                      <input className={fieldClass} value={moduleForm.title}
                        disabled={moduleBusy}
                        onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})}
                        placeholder="e.g. Module 1: Introduction" />
                    </div>
                    <div className="mt-4">
                      <span className={labelClass}>Description (optional)</span>
                      <RichTextField
                        value={moduleForm.description}
                        disabled={moduleBusy}
                        minHeight={100}
                        placeholder="What this module covers."
                        onChange={(description) => setModuleForm((prev) => ({...prev, description}))}
                      />
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" className={primaryBtn} disabled={moduleBusy}>
                        <FontAwesomeIcon icon={view.kind === "module-edit" ? faPen : faPlus} />
                        {moduleBusy ? "Saving…" : view.kind === "module-edit" ? "Save module" : "Create module"}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                /* ── Create / edit lesson ──────────────────────────────────── */
                <div className="mt-4 rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="m-0 text-xs font-bold text-slate-900">
                        {assessmentEditorOpen
                          ? `Edit ${createdAssessmentKind.toLowerCase()} assessment`
                          : view.kind === "lesson-edit" ? "Edit lesson" : "Add a lesson"}
                      </h3>
                      <p className="m-0 mt-0.5 text-xs text-slate-500">
                        {assessmentEditorOpen
                          ? `Assessment for “${lessonForm.title || "this lesson"}”.`
                          : activeModule
                          ? `Adding to “${activeModule.title}”.`
                          : "This lesson has no module yet — choose one."}
                      </p>
                    </div>
                    {assessmentEditorOpen ? (
                      <button
                        type="button"
                        className={`${ghostBtn} ml-auto`}
                        onClick={() => setAssessmentEditorOpen(false)}
                      >
                        Back to lesson
                      </button>
                    ) : (
                      <button type="button" className={`${ghostBtn} ml-auto`} onClick={leaveLessonForm}>Cancel</button>
                    )}
                  </div>
                  <form className="mt-3" onSubmit={saveLesson}>
                    {assessmentEditorOpen ? (
                      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        {!createdAssessmentId ? (
                          <p className="m-0 text-sm text-amber-800">
                            The assessment was saved, but its ID was not returned. Reopen the lesson to reload it before editing.
                          </p>
                        ) : (
                          <>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Assessment title
                                <input
                                  className={fieldClass}
                                  value={lessonForm.assessmentTitle}
                                  disabled={lessonFormDisabled}
                                  onChange={(event) => changeAssessmentForm("assessmentTitle", event.target.value)}
                                />
                              </label>
                              {createdAssessmentKind === "QUIZ" ? (
                                <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                  Pass threshold (%)
                                  <input
                                    className={fieldClass}
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={lessonForm.assessmentPassThreshold}
                                    disabled={lessonFormDisabled}
                                    onChange={(event) =>
                                      changeAssessmentForm("assessmentPassThreshold", event.target.value)
                                    }
                                  />
                                </label>
                              ) : null}
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
                                {createdAssessmentKind === "QUIZ" ? "Instructions" : "Question / task"}
                                <textarea
                                  className={fieldClass}
                                  rows={4}
                                  value={lessonForm.assessmentInstructions}
                                  disabled={lessonFormDisabled}
                                  onChange={(event) =>
                                    changeAssessmentForm("assessmentInstructions", event.target.value)
                                  }
                                />
                              </label>
                              <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-2">
                                <input
                                  type="checkbox"
                                  checked={lessonForm.assessmentIsRequired}
                                  disabled={lessonFormDisabled}
                                  onChange={(event) =>
                                    changeAssessmentForm("assessmentIsRequired", event.target.checked)
                                  }
                                />
                                Required assessment
                              </label>
                            </div>
                            {createdAssessmentKind === "QUIZ" ? (
                              <LessonQuizQuestionsPanel
                                courseId={id}
                                lessonId={editingLessonId}
                                assessmentId={createdAssessmentId}
                                disabled={locked}
                                onError={fail}
                                onSuccess={ok}
                              />
                            ) : null}
                          </>
                        )}
                        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-3">
                          <button
                            type="button"
                            className={ghostBtn}
                            disabled={lessonMutationLoading}
                            onClick={() => setAssessmentEditorOpen(false)}
                          >
                            Back to lesson
                          </button>
                          {createdAssessmentId ? (
                            <button
                              type="button"
                              className={primaryBtn}
                              disabled={!assessmentDirty || lessonFormDisabled}
                              onClick={saveAssessment}
                            >
                              Save assessment
                            </button>
                          ) : null}
                        </div>
                      </section>
                    ) : (
                    <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass} htmlFor="lesson-title">Lesson title</label>
                        <input
                          id="lesson-title"
                          className={fieldClass}
                          value={lessonForm.title}
                          disabled={lessonFormDisabled}
                          onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})}
                          placeholder="Lesson title"
                        />
                      </div>
                      <div>
                        <label className={labelClass} htmlFor="lesson-content-type">Content type</label>
                        <select
                          id="lesson-content-type"
                          className={fieldClass}
                          value={lessonForm.contentType}
                          disabled={lessonFormDisabled}
                          onChange={(event) =>
                            setLessonForm((current) => ({...current, contentType: event.target.value}))
                          }
                        >
                          <option value="TEXT">Text</option>
                          <option value="FILE">File</option>
                          <option value="VIDEO">Video</option>
                        </select>
                      </div>
                      {/* Drilling in already fixes the module, so the picker is
                          only needed to re-home a legacy ungrouped lesson. */}
                      {!activeModule ? (
                        <select className={`${fieldClass} sm:col-span-2`} value={lessonForm.moduleId} disabled={lessonFormDisabled}
                          onChange={(e) => setLessonForm({...lessonForm, moduleId: e.target.value})}>
                          <option value="">Choose a module…</option>
                          {(modules || []).map((module) => (
                            <option key={module.id} value={module.id}>{module.title}</option>
                          ))}
                        </select>
                      ) : null}
                      <div className="sm:col-span-2">
                        <label className={labelClass}>Short description</label>
                        <input className={fieldClass} value={lessonForm.description} disabled={lessonFormDisabled}
                          onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})}
                          placeholder="A short summary of this lesson (optional)" />
                      </div>
                      <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={Boolean(lessonForm.isPreview)}
                            disabled={lessonFormDisabled}
                            onChange={(event) => setLessonForm({...lessonForm, isPreview: event.target.checked})}
                          />
                          Free preview lesson
                        </label>
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={Boolean(lessonForm.isRequired)}
                            disabled={lessonFormDisabled}
                            onChange={(event) => setLessonForm({...lessonForm, isRequired: event.target.checked})}
                          />
                          Required lesson
                        </label>
                      </div>
                      <div className="sm:col-span-2">
                        {lessonForm.contentType === "TEXT" ? (
                          <>
                            <label className={labelClass}>Lesson text</label>
                            <RichTextField
                              value={lessonForm.content}
                              disabled={lessonFormDisabled}
                              minHeight={140}
                              placeholder="Write the lesson text. Formatting is supported."
                              onChange={(content) => setLessonForm((prev) => ({...prev, content}))}
                            />
                          </>
                        ) : null}
                        {lessonForm.contentType === "FILE" ? (
                          <div className="grid gap-2">
                            <label className={labelClass} htmlFor="lesson-file-name">File name</label>
                            <input
                              id="lesson-file-name"
                              className={fieldClass}
                              value={lessonForm.fileName}
                              disabled={lessonFormDisabled}
                              onChange={(event) =>
                                setLessonForm((current) => ({...current, fileName: event.target.value}))
                              }
                              placeholder="e.g. Lesson notes"
                            />
                            <UploadField
                              kind="document"
                              label="Lesson file"
                              accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                              value={lessonForm.fileUrl}
                              disabled={lessonFormDisabled}
                              onChange={(url, upload) =>
                                setLessonForm((current) => ({
                                  ...current,
                                  fileUrl: url || "",
                                  fileName: upload?.fileName || upload?.file_name || upload?.name || current.fileName,
                                  filePublicId: upload?.publicId || upload?.public_id || "",
                                  fileCloudType: upload?.cloudType || upload?.cloud_type || "",
                                  fileDeliveryType: upload?.deliveryType || upload?.delivery_type || "",
                                }))
                              }
                            />
                            {editingLessonId && lessonAttachedResources.length ? (
                              <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2">
                                <p className="m-0 mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                  Attached files ({lessonAttachedResources.length})
                                </p>
                                <ul className="m-0 grid list-none gap-1 p-0">
                                  {lessonAttachedResources.map((resource, index) => (
                                    <li key={resource.id} className="flex items-center gap-2">
                                      <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                                        {resource.name || `File ${index + 1}`}
                                      </span>
                                      <button type="button" className={ghostBtn} title="Move up"
                                        disabled={lessonFormDisabled || index === 0}
                                        onClick={() => moveAttachedResource(index, -1)}>
                                        <FontAwesomeIcon icon={faArrowUp} />
                                      </button>
                                      <button type="button" className={ghostBtn} title="Move down"
                                        disabled={lessonFormDisabled || index === lessonAttachedResources.length - 1}
                                        onClick={() => moveAttachedResource(index, 1)}>
                                        <FontAwesomeIcon icon={faArrowDown} />
                                      </button>
                                      <button type="button" className={ghostBtn} title="Delete file"
                                        disabled={lessonFormDisabled}
                                        onClick={() => removeAttachedResource(resource)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {lessonForm.contentType === "VIDEO" ? (
                          <div className="grid gap-2">
                            <label className={labelClass} htmlFor="lesson-video-title">Video title</label>
                            <input
                              id="lesson-video-title"
                              className={fieldClass}
                              value={lessonForm.videoTitle}
                              disabled={lessonFormDisabled}
                              onChange={(event) =>
                                setLessonForm((current) => ({...current, videoTitle: event.target.value}))
                              }
                              placeholder="e.g. Introduction to the topic"
                            />
                            <label className={labelClass} htmlFor="lesson-video-url">Video URL</label>
                            <input
                              id="lesson-video-url"
                              type="url"
                              className={fieldClass}
                              value={lessonForm.videoUrl}
                              disabled={lessonFormDisabled}
                              onChange={(event) =>
                                setLessonForm((current) => ({...current, videoUrl: event.target.value}))
                              }
                              placeholder="https://youtube.com/…"
                            />
                            <p className="m-0 text-xs text-slate-500">
                              The video is saved separately from the lesson body.
                            </p>
                            {editingLessonId && lessonAttachedVideos.length ? (
                              <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2">
                                <p className="m-0 mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                  Attached videos ({lessonAttachedVideos.length})
                                </p>
                                <ul className="m-0 grid list-none gap-1 p-0">
                                  {lessonAttachedVideos.map((video, index) => (
                                    <li key={video.id} className="flex items-center gap-2">
                                      <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                                        {video.title || `Video ${index + 1}`}
                                      </span>
                                      <button type="button" className={ghostBtn} title="Move up"
                                        disabled={lessonFormDisabled || index === 0}
                                        onClick={() => moveAttachedVideo(index, -1)}>
                                        <FontAwesomeIcon icon={faArrowUp} />
                                      </button>
                                      <button type="button" className={ghostBtn} title="Move down"
                                        disabled={lessonFormDisabled || index === lessonAttachedVideos.length - 1}
                                        onClick={() => moveAttachedVideo(index, 1)}>
                                        <FontAwesomeIcon icon={faArrowDown} />
                                      </button>
                                      <button type="button" className={ghostBtn} title="Delete video"
                                        disabled={lessonFormDisabled}
                                        onClick={() => removeAttachedVideo(video)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <section className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div>
                          <h4 className="m-0 text-sm font-semibold text-slate-900">Lesson assessment</h4>
                          <p className="m-0 mt-1 text-xs text-slate-500">
                            Assessments for this lesson are managed here. Select an existing one to edit it, or add another.
                          </p>
                        </div>
                        {lessonAssessments.length ? (
                          <ul className="mb-0 mt-3 grid gap-2 p-0 sm:grid-cols-2">
                            {lessonAssessments.map((assessment) => {
                              const assessmentId = assessment.id || assessment._id;
                              const selected = String(createdAssessmentId) === String(assessmentId);
                              return (
                                <li key={assessmentId || `${assessment.kind}-${assessment.title}`} className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className={`min-w-0 flex-1 rounded-md border px-3 py-2 text-left text-xs ${
                                      selected
                                        ? "border-blue-300 bg-blue-50 text-blue-900"
                                        : "border-slate-200 bg-white text-slate-700"
                                    }`}
                                    disabled={!assessmentId || lessonFormDisabled}
                                    onClick={() => selectAssessment(assessment)}
                                  >
                                    <span className="block font-semibold">{assessment.title || "Untitled assessment"}</span>
                                    <span className="mt-0.5 block text-[11px] opacity-75">
                                      {String(assessment.kind || "ASSESSMENT").toUpperCase()}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className={ghostBtn}
                                    title="Move up"
                                    disabled={lessonFormDisabled || lessonAssessments.indexOf(assessment) === 0}
                                    onClick={() => moveAssessment(lessonAssessments.indexOf(assessment), -1)}
                                  >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                  </button>
                                  <button
                                    type="button"
                                    className={ghostBtn}
                                    title="Move down"
                                    disabled={
                                      lessonFormDisabled ||
                                      lessonAssessments.indexOf(assessment) === lessonAssessments.length - 1
                                    }
                                    onClick={() => moveAssessment(lessonAssessments.indexOf(assessment), 1)}
                                  >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                  </button>
                                  <button
                                    type="button"
                                    className={ghostBtn}
                                    disabled={lessonFormDisabled}
                                    aria-label={`Delete ${assessment.title || "assessment"}`}
                                    onClick={() => removeAssessment(assessment)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button type="button" className={ghostBtn} disabled={lessonFormDisabled} onClick={startNewAssessment}>
                            <FontAwesomeIcon icon={faPlus} /> Add assessment
                          </button>
                        </div>
                        {!assessmentCreated ? (
                          <>
                            <label className={`${labelClass} mt-3`} htmlFor="lesson-assessment-kind">
                              Assessment type
                            </label>
                            <select
                              id="lesson-assessment-kind"
                              className={fieldClass}
                              value={lessonForm.assessmentKind}
                              disabled={lessonFormDisabled}
                              onChange={(event) => {
                                changeAssessmentForm("assessmentKind", event.target.value);
                                changeAssessmentForm("assessmentTitle", "");
                                changeAssessmentForm("assessmentInstructions", "");
                              }}
                            >
                              <option value="">No assessment</option>
                              <option value="ASSIGNMENT">Assignment</option>
                              <option value="QUIZ">Quiz</option>
                              <option value="PRACTICAL">Practical</option>
                              <option value="REPORT">Report</option>
                            </select>
                          </>
                        ) : null}
                        {!assessmentCreated && lessonForm.assessmentKind ? (
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                              Assessment title
                              <input
                                className={fieldClass}
                                value={lessonForm.assessmentTitle}
                                disabled={lessonFormDisabled}
                                onChange={(event) => changeAssessmentForm("assessmentTitle", event.target.value)}
                                placeholder={
                                  lessonForm.assessmentKind === "QUIZ"
                                    ? "e.g. Module 1 quiz"
                                    : `e.g. ${lessonForm.assessmentKind.toLowerCase()} task`
                                }
                              />
                            </label>
                            {lessonForm.assessmentKind === "QUIZ" ? (
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Pass threshold (%)
                                <input
                                  className={fieldClass}
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={lessonForm.assessmentPassThreshold}
                                  disabled={lessonFormDisabled}
                                  onChange={(event) => changeAssessmentForm("assessmentPassThreshold", event.target.value)}
                                />
                              </label>
                            ) : null}
                            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
                              {lessonForm.assessmentKind === "QUIZ" ? "Instructions" : "Question / task"}
                              <textarea
                                className={fieldClass}
                                rows={3}
                                value={lessonForm.assessmentInstructions}
                                disabled={lessonFormDisabled}
                                onChange={(event) => changeAssessmentForm("assessmentInstructions", event.target.value)}
                                placeholder={
                                  lessonForm.assessmentKind === "QUIZ"
                                    ? "Describe what learners should do before answering the quiz."
                                    : "Enter the question or task requirements for learners."
                                }
                              />
                            </label>
                            <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-2">
                              <input
                                type="checkbox"
                                checked={lessonForm.assessmentIsRequired}
                                disabled={lessonFormDisabled}
                                onChange={(event) => changeAssessmentForm("assessmentIsRequired", event.target.checked)}
                              />
                              Required assessment
                            </label>
                          </div>
                        ) : null}

                        {assessmentCreated && !createdAssessmentId ? (
                          <p className="mb-0 mt-3 text-xs text-amber-800">
                            The assessment was saved, but its ID was not returned. Reopen this lesson to reload it.
                          </p>
                        ) : null}
                      </section>
                    </div>
                    </>
                    )}
                      {/* The bottom action is the one that actually fires POST
                          …/lessons. It is labelled "Create lesson" rather than "Add
                          lesson" so it is distinguishable from the top-right button
                          of the same old label, which only opens this form. */}
                      {!assessmentEditorOpen ? (
                      <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
                        {assessmentCreated && editingLessonId ? (
                          <button
                            type="button"
                            className={ghostBtn}
                            onClick={() => showLessons(lessonForm.moduleId || activeModuleId)}
                          >
                            Done
                          </button>
                        ) : null}
                        <button
                          type="submit"
                          className={primaryBtn}
                          disabled={lessonFormDisabled}
                        >
                          <FontAwesomeIcon icon={editingLessonId ? faPen : faPlus} />
                          {lessonMutationLoading
                            ? "Saving…"
                            : lessonForm.assessmentKind && !assessmentCreated
                              ? "Save lesson and assessment"
                              : editingLessonId
                                ? "Save lesson"
                                : "Create lesson"}
                        </button>
                      </div>
                      ) : null}
                    {lessonMutationError ? (
                      <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">{lessonMutationError}</p>
                    ) : null}
                  </form>
                </div>
              )}
            </>
          )}
          </>
        )}
      </section>
    </ResourcePersonShell>
  );
};

export default ResourcePersonCourseBuilder;
